<template>
  <div class="profile-container">
    <AppHeader title="User Profile" back-to="/" back-text="Back to Devices" />

    <main class="main-content">
      <div v-if="user" class="profile-content">
        <div class="user-info-section">
          <div class="user-details">
            <h2>{{ user.displayName || user.email }}</h2>
            <p class="email">{{ user.email }}</p>
            <span class="role-badge" :class="userRole">{{ userRole }}</span>
          </div>
          <router-link v-if="userRole === 'admin'" to="/users" class="manage-users-btn">
            Manage Users
          </router-link>
        </div>

        <div class="my-devices-section">
          <h3>My Devices</h3>
          <div v-if="myDevices.length > 0" class="devices-grid">
            <DeviceCard v-for="device in myDevices" :key="device.id" :device="device" />
          </div>
          <p v-else class="no-devices">No devices currently taken</p>
        </div>

        <AdminDashboard v-if="userRole === 'admin'" />
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { auth, db } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { COLORS, FONTS, GRADIENTS } from '@/styles/brand'
import { createOrGetUser } from '../services/userService'
import AdminDashboard from './AdminDashboard.vue'
import AppHeader from './AppHeader.vue'
import DeviceCard from './DeviceCard.vue'

const user = ref(null)
const userRole = ref('user')
const myDevices = ref([])

const loadMyDevices = async () => {
  if (user.value) {
    const q = query(collection(db, 'devices'), where('lastTakenBy', '==', user.value.email))
    const querySnapshot = await getDocs(q)
    myDevices.value = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  }
}



onMounted(() => {
  onAuthStateChanged(auth, async (currentUser) => {
    if (currentUser && currentUser.email.endsWith('@nordeus.com')) {
      user.value = currentUser
      const userData = await createOrGetUser(currentUser)
      userRole.value = userData.role
      await loadMyDevices()
    }
  })
})
</script>

<style scoped>
.profile-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(20, 64, 154, 0.8) 50%, rgba(31, 31, 79, 0.9) 100%),
    url('@/assets/nordeus-3840x1080-pattern_new.jpg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  font-family: v-bind('FONTS.GOOGLE');
}



.main-content {
  flex: 1;
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

.user-info-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;
  background: v-bind('GRADIENTS.CARD');
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 32px;
  border-radius: 20px;
}

.manage-users-btn {
  padding: 12px 24px;
  background: v-bind('COLORS.PRIMARY.RED');
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.manage-users-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.user-avatar img {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid v-bind('COLORS.PRIMARY.WHITE');
}

.user-details h2 {
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-family: v-bind('FONTS.PRIMARY');
  margin: 0 0 8px 0;
  font-size: 1.8rem;
}

.email {
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 12px 0;
}

.role-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
}

.role-badge.admin {
  background: v-bind('COLORS.PRIMARY.RED');
  color: white;
}

.role-badge.user {
  background: rgba(255, 255, 255, 0.2);
  color: v-bind('COLORS.PRIMARY.WHITE');
}

.my-devices-section {
  margin-bottom: 40px;
  background: v-bind('GRADIENTS.CARD');
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 32px;
  border-radius: 20px;
}

.my-devices-section h3 {
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-family: v-bind('FONTS.PRIMARY');
  margin: 0 0 24px 0;
  font-size: 1.5rem;
}

.devices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.no-devices {
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  margin: 0;
}




</style>
